import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

interface WebLogoProps {
  sequences: string[];
  sequenceType: 'protein' | 'dna' | 'rna';
  width?: number;
  height?: number;
}

const WebLogo: React.FC<WebLogoProps> = ({ 
  sequences, 
  sequenceType, 
  width = 1200, 
  height = 600 
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [svgWidth, setSvgWidth] = React.useState<number>(width);

  // 氨基酸颜色方案 (Berkeley WebLogo 标准)
  const aminoAcidColors: Record<string, string> = {
    // 疏水性氨基酸 - 黑色
    'A': '#000000', 'I': '#000000', 'L': '#000000', 'M': '#000000', 'F': '#000000', 'W': '#000000', 'Y': '#000000', 'V': '#000000',
    // 极性氨基酸 - 绿色
    'N': '#00FF00', 'C': '#00FF00', 'Q': '#00FF00', 'S': '#00FF00', 'T': '#00FF00',
    // 碱性氨基酸 - 蓝色
    'R': '#0000FF', 'H': '#0000FF', 'K': '#0000FF',
    // 酸性氨基酸 - 红色
    'D': '#FF0000', 'E': '#FF0000',
    // 甘氨酸和脯氨酸 - 橙色
    'G': '#FF7F00', 'P': '#FF7F00',
    // 空位
    '-': '#CCCCCC'
  };

  // 核酸颜色方案
  const nucleotideColors: Record<string, string> = {
    'A': '#FF0000', // 红色
    'T': '#0000FF', // 蓝色
    'U': '#0000FF', // 蓝色 (RNA)
    'G': '#FFB000', // 橙色
    'C': '#00FF00', // 绿色
    '-': '#CCCCCC'  // 灰色
  };

  const getColorScheme = () => {
    return sequenceType === 'protein' ? aminoAcidColors : nucleotideColors;
  };

  // 计算信息内容和频率
  const calculatePositionData = () => {
    if (!sequences || sequences.length === 0) return [];
    
    const seqLength = sequences[0].length;
    const positionData = [];
    
    for (let pos = 0; pos < seqLength; pos++) {
      const charCounts: Record<string, number> = {};
      const totalSeqs = sequences.length;
      
      // 统计每个位置的字符频率
      sequences.forEach(seq => {
        const char = seq[pos]?.toUpperCase() || '-';
        charCounts[char] = (charCounts[char] || 0) + 1;
      });
      
      // 计算频率和信息内容 (包含gaps)
      const chars = Object.keys(charCounts); // 包含所有字符，包括'-'
      const totalChars = chars.reduce((sum, char) => sum + charCounts[char], 0);
      
      if (totalChars === 0) {
        positionData.push({ position: pos + 1, chars: [], informationContent: 0 });
        continue;
      }
      
      // 计算信息内容 (Shannon entropy) - 包含gaps
      const alphabetSize = sequenceType === 'protein' ? 21 : 5; // 增加1个位置给gap
      const maxEntropy = Math.log2(alphabetSize);
      
      let entropy = 0;
      chars.forEach(char => {
        const freq = charCounts[char] / totalChars;
        if (freq > 0) {
          entropy -= freq * Math.log2(freq);
        }
      });
      
      const informationContent = maxEntropy - entropy;
      
      // 按频率排序字符 (包含gaps)
      const sortedChars = chars
        .map(char => ({
          char,
          frequency: charCounts[char] / totalChars,
          count: charCounts[char]
        }))
        .sort((a, b) => b.frequency - a.frequency);
      
      positionData.push({
        position: pos + 1,
        chars: sortedChars,
        informationContent: Math.max(0, informationContent)
      });
    }
    
    return positionData;
  };

  useEffect(() => {
    if (!svgRef.current || !sequences || sequences.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const positionData = calculatePositionData();
    const maxInformationContent = Math.max(...positionData.map(d => d.informationContent));
    
    if (maxInformationContent === 0) return;

    const margin = { top: 30, right: 30, bottom: 100, left: 60 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = (height - 30) - margin.top - margin.bottom;

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // 添加缩放功能
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.5, 5])
      .on('zoom', (event) => {
        g.attr('transform', `translate(${margin.left},${margin.top}) ${event.transform}`);
      });

    svg.call(zoom);

    // 动态计算每个位置需要的宽度
    const positionWidth = 60; // 每个位置的基础宽度
    const spacing = 20; // 位置间的间距
    const totalNeededWidth = positionData.length * (positionWidth + spacing);
    const actualChartWidth = Math.max(chartWidth, totalNeededWidth);
    // 计算并设置SVG实际需要的宽度（包含边距）
    const requiredSvgWidth = Math.max(width, actualChartWidth + margin.left + margin.right);
    setSvgWidth(requiredSvgWidth);
    
    // X轴比例尺
    const xScale = d3.scaleBand()
      .domain(positionData.map(d => d.position.toString()))
      .range([0, actualChartWidth])
      .paddingInner(spacing / (positionWidth + spacing))
      .paddingOuter(0.1);

    // Y轴比例尺
    const yScale = d3.scaleLinear()
      .domain([0, maxInformationContent])
      .range([chartHeight, 0]);

    const colorScheme = getColorScheme();

    // 绘制每个位置的字符堆叠
    positionData.forEach(posData => {
      if (posData.chars.length === 0) return;
      
      const x = xScale(posData.position.toString()) || 0;
      const barWidth = xScale.bandwidth();
      
      let currentY = chartHeight;
      
      posData.chars.forEach(charData => {
        // 字符高度 = 频率 × 信息内容，按比例缩放到图表高度
        const charHeight = (charData.frequency * posData.informationContent / maxInformationContent) * chartHeight;
        
        if (charHeight > 3) {
          // 使用SVG transform来拉伸字符，保持字符可读性
          const baseFontSize = Math.min(barWidth * 0.7, 18);
          const scaleY = charHeight / baseFontSize;
          
          // 创建字符组
          const charGroup = g.append('g');
          
          // 添加字符文本
          charGroup.append('text')
            .attr('x', x + barWidth / 2)
            .attr('y', currentY - charHeight / 2)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .attr('font-family', 'Arial, sans-serif')
            .attr('font-size', baseFontSize)
            .attr('font-weight', 'bold')
            .attr('fill', colorScheme[charData.char] || '#000000')
            .text(charData.char)
            .attr('transform', `translate(${x + barWidth / 2}, ${currentY - charHeight / 2}) scale(6, ${Math.max(0.5, scaleY)}) translate(${-(x + barWidth / 2)}, ${-(currentY - charHeight / 2)})`);
          
          currentY -= charHeight;
        }
      });
    });

    // X轴
    g.append('g')
      .attr('transform', `translate(0,${chartHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .style('font-size', '12px')
      .attr('transform', 'rotate(-45)')
      .style('text-anchor', 'end');

    // Y轴
    g.append('g')
      .call(d3.axisLeft(yScale).ticks(5))
      .selectAll('text')
      .style('font-size', '12px');

    // Y轴标签
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 0 - margin.left + 15)
      .attr('x', 0 - (chartHeight / 2))
      .attr('dy', '1em')
      .style('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .text('信息内容 (bits)');

    // X轴标签
    g.append('text')
      .attr('transform', `translate(${actualChartWidth / 2}, ${chartHeight + margin.bottom - 20})`)
      .style('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .text('位置');

  }, [sequences, sequenceType, width, height]);

  return (
    <div style={{ width: '100%', height: `${height}px`, overflowX: 'auto', overflowY: 'hidden' }}>
      <div style={{ marginBottom: '10px', fontSize: '12px', color: '#666' }}>
        提示：可以使用鼠标滚轮缩放，拖拽平移查看详细内容
      </div>
      <div style={{ width: `${svgWidth}px`, height: `${height - 30}px`, overflow: 'hidden' }}>
        <svg
          ref={svgRef}
          width={svgWidth}
          height={height - 30}
          style={{ border: '1px solid #e8e8e8', borderRadius: '4px', cursor: 'grab', display: 'block' }}
        />
      </div>
    </div>
  );
};

export default WebLogo;
